import { useState, useRef, useEffect } from 'react'
import { FiX, FiSmartphone, FiCheck, FiAlertCircle, FiPlus, FiLink, FiChevronLeft } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

// Ícone Meta API
const MetaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
  </svg>
)

// Ícone WhatsApp
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
)

interface NewDevicePanelProps {
  mode: 'evolution' | 'meta'
  onClose: () => void
  onDeviceCreated?: () => void
}

// Constantes Evolution
const QR_TIMEOUT_SECONDS = 60
const POLLING_INTERVAL_MS = 2000

// URL que a Meta (ou a BSP) tem que chamar. Vinha fixa em `ia.twms.com.br`, que
// é a VPS antiga — quem seguisse a tela apontaria o webhook pro servidor errado,
// e no Planalto apontaria pra matriz. A origem atual é sempre a certa: quem
// cadastra o device está logado exatamente no CRM que vai receber as mensagens.
const METAWEBHOOK_URL = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/meta/webhook`

export function NewDevicePanel({ mode, onClose, onDeviceCreated }: NewDevicePanelProps) {
  const { authenticatedFetch } = useAuth()

  // ============================================
  // META API STATE
  // ============================================
  const [metaForm, setMetaForm] = useState({
    name: '',
    phoneNumberId: '',
    businessAccountId: '',
    accessToken: '',
    displayPhone: ''
  })
  const [metaLoading, setMetaLoading] = useState(false)
  const [metaError, setMetaError] = useState<string | null>(null)
  const [metaSuccess, setMetaSuccess] = useState(false)

  // ============================================
  // EVOLUTION STATE
  // ============================================
  const [evolutionStep, setEvolutionStep] = useState<'choice' | 'new' | 'existing'>('choice')
  const [newInstanceName, setNewInstanceName] = useState('')
  const [newInstancePhone, setNewInstancePhone] = useState('')
  const [existingInstanceName, setExistingInstanceName] = useState('')
  const [existingInstanceApiKey, setExistingInstanceApiKey] = useState('')
  const [isCreatingInstance, setIsCreatingInstance] = useState(false)
  const [isSavingExisting, setIsSavingExisting] = useState(false)
  const [pendingQRCode, setPendingQRCode] = useState<string | null>(null)
  const [pendingPairingCode, setPendingPairingCode] = useState<string | null>(null)
  const [pendingInstanceName, setPendingInstanceName] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'waiting' | 'connected' | 'expired'>('idle')
  const [countdown, setCountdown] = useState(QR_TIMEOUT_SECONDS)
  const [evolutionError, setEvolutionError] = useState<string | null>(null)
  const [evolutionSuccess, setEvolutionSuccess] = useState<string | null>(null)

  // Refs
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  // Evolution API removida em 2026-05-14 — stack opera só com Meta API.
  // Stub mantido pra qualquer caminho antigo da UI que ainda referencie:
  // chamadas falham com erro claro em vez de hit numa Evolution decommissionada.
  // Sem chaves hardcoded no bundle do cliente.
  const evolutionFetch = async (_endpoint: string, _options: RequestInit = {}): Promise<Response> => {
    throw new Error('Evolution API foi removida em 2026-05-14. Use Meta API.')
  }

  // ============================================
  // CLEANUP
  // ============================================
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  const resetQRState = () => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    if (pollingRef.current) clearInterval(pollingRef.current)
    setPendingQRCode(null)
    setPendingPairingCode(null)
    setPendingInstanceName(null)
    setConnectionStatus('idle')
    setCountdown(QR_TIMEOUT_SECONDS)
    setIsCreatingInstance(false)
    setNewInstanceName('')
    setNewInstancePhone('')
  }

  // ============================================
  // META API HANDLERS
  // ============================================
  const handleMetaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMetaLoading(true)
    setMetaError(null)
    setMetaSuccess(false)

    try {
      const response = await authenticatedFetch('/api/meta/devices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metaForm)
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erro ao cadastrar dispositivo')
      }

      setMetaSuccess(true)
      setMetaForm({
        name: '',
        phoneNumberId: '',
        businessAccountId: '',
        accessToken: '',
        displayPhone: ''
      })

      setTimeout(() => {
        onDeviceCreated?.()
        onClose()
      }, 1500)
    } catch (err: any) {
      setMetaError(err.message)
    } finally {
      setMetaLoading(false)
    }
  }

  // ============================================
  // EVOLUTION HANDLERS
  // ============================================
  const createEvolutionInstance = async () => {
    if (!newInstanceName.trim()) return

    setIsCreatingInstance(true)
    setEvolutionError(null)

    try {
      const instanceName = newInstanceName.trim().toLowerCase().replace(/\s+/g, '-')

      // Criar instância
      const createResponse = await evolutionFetch('/instance/create', {
        method: 'POST',
        body: JSON.stringify({
          instanceName,
          qrcode: true,
          integration: 'WHATSAPP-BAILEYS',
          number: newInstancePhone || undefined
        })
      })

      if (!createResponse.ok) {
        const error = await createResponse.json()
        throw new Error(error.message || 'Erro ao criar instância')
      }

      const createResult = await createResponse.json()
      let qrCodeBase64 = createResult.qrcode?.base64 || createResult.base64
      let pairingCode = createResult.qrcode?.pairingCode || createResult.pairingCode

      // Se não veio QR, buscar connect
      if (!qrCodeBase64) {
        const qrResponse = await evolutionFetch(`/instance/connect/${instanceName}`)
        if (qrResponse.ok) {
          const qrResult = await qrResponse.json()
          qrCodeBase64 = qrResult.base64 || qrResult.code || (qrResult.qrcode?.base64)
          pairingCode = qrResult.pairingCode
        }
      }

      if (qrCodeBase64) {
        const qrCodeUrl = qrCodeBase64.startsWith('data:image')
          ? qrCodeBase64
          : `data:image/png;base64,${qrCodeBase64}`

        setPendingQRCode(qrCodeUrl)
        setPendingPairingCode(pairingCode)
        setPendingInstanceName(instanceName)
        setConnectionStatus('waiting')
        setCountdown(QR_TIMEOUT_SECONDS)
        startCountdown()
        startConnectionPolling(instanceName)
      } else {
        setEvolutionSuccess('Instância criada!')
        resetQRState()
        onDeviceCreated?.()
      }
    } catch (error: any) {
      setEvolutionError(error.message)
      setIsCreatingInstance(false)
    }
  }

  const startCountdown = () => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    setCountdown(QR_TIMEOUT_SECONDS)

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleQRExpired()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleQRExpired = async () => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    if (pollingRef.current) clearInterval(pollingRef.current)
    setConnectionStatus('expired')

    if (pendingInstanceName) {
      try {
        await evolutionFetch(`/instance/delete/${pendingInstanceName}`, { method: 'DELETE' })
      } catch (error) {
        console.error('Erro ao deletar instância expirada:', error)
      }
    }
  }

  const startConnectionPolling = (instanceName: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current)

    pollingRef.current = setInterval(async () => {
      try {
        const response = await evolutionFetch(`/instance/connectionState/${instanceName}`)
        if (!response.ok) return

        const data = await response.json()
        const state = data?.instance?.state || data?.state

        if (state === 'open') {
          const infoResponse = await evolutionFetch(`/instance/fetchInstances?instanceName=${instanceName}`)
          let instanceData = null
          if (infoResponse.ok) {
            const infoResult = await infoResponse.json()
            instanceData = Array.isArray(infoResult)
              ? infoResult.find((i: any) => i.name === instanceName)
              : infoResult.instance || infoResult
          }
          handleConnectionSuccess(instanceName, instanceData || { name: instanceName })
        }
      } catch (error) {
        console.log('Polling error:', error)
      }
    }, POLLING_INTERVAL_MS)
  }

  const handleConnectionSuccess = async (instanceName: string, instanceData: any) => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    if (pollingRef.current) clearInterval(pollingRef.current)
    setConnectionStatus('connected')

    // Configurar webhook
    const webhookUrl = `${window.location.origin}/api/webhook/evolution`
    try {
      await evolutionFetch(`/webhook/set/${instanceName}`, {
        method: 'POST',
        body: JSON.stringify({
          webhook: {
            url: webhookUrl,
            enabled: true,
            webhookByEvents: false,
            webhookBase64: true,
            events: ['MESSAGES_UPSERT', 'CONNECTION_UPDATE', 'MESSAGES_UPDATE', 'SEND_MESSAGE']
          }
        })
      })
    } catch (e) { console.error('Webhook config error:', e) }

    // Salvar no banco
    const newDevice = {
      id: `device_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      name: instanceName,
      connection_status: 'open',
      owner_jid: instanceData.ownerJid || '',
      profile_name: instanceData.profileName || instanceName,
      profile_pic_url: instanceData.profilePicUrl || '',
      ai_agent_enabled: false
    }

    await authenticatedFetch('/api/whatsapp-devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newDevice)
    })

    setEvolutionSuccess(`${instanceData.profileName || instanceName} conectado!`)
    setTimeout(() => {
      onDeviceCreated?.()
      onClose()
    }, 1500)
  }

  const handleSaveExistingInstance = async () => {
    if (!existingInstanceName.trim() || !existingInstanceApiKey.trim()) return

    setIsSavingExisting(true)
    setEvolutionError(null)

    try {
      // Verificar se instância existe
      const response = await evolutionFetch(`/instance/connectionState/${existingInstanceName}`)
      if (!response.ok) throw new Error('Instância não encontrada')

      const data = await response.json()
      const state = data?.instance?.state || data?.state
      if (state !== 'open') throw new Error('Instância não está conectada')

      // Buscar info
      const infoResponse = await evolutionFetch(`/instance/fetchInstances?instanceName=${existingInstanceName}`)
      let instanceData = null
      if (infoResponse.ok) {
        const infoResult = await infoResponse.json()
        instanceData = Array.isArray(infoResult)
          ? infoResult.find((i: any) => i.name === existingInstanceName)
          : infoResult.instance || infoResult
      }

      // Salvar no banco
      const newDevice = {
        id: `device_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        name: existingInstanceName,
        connection_status: 'open',
        owner_jid: instanceData?.ownerJid || '',
        profile_name: instanceData?.profileName || existingInstanceName,
        profile_pic_url: instanceData?.profilePicUrl || '',
        ai_agent_enabled: false
      }

      await authenticatedFetch('/api/whatsapp-devices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDevice)
      })

      setEvolutionSuccess('Instância adicionada!')
      setTimeout(() => {
        onDeviceCreated?.()
        onClose()
      }, 1500)
    } catch (error: any) {
      setEvolutionError(error.message)
    } finally {
      setIsSavingExisting(false)
    }
  }

  // ============================================
  // RENDER META
  // ============================================
  if (mode === 'meta') {
    return (
      <div className="flex flex-col h-full bg-card neu-flat">
        {/* Header */}
        <div className="neu-flat flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
              <MetaIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Novo Dispositivo Meta</h2>
              <p className="text-xs text-muted-foreground">WhatsApp Business API Oficial</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Fechar"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Webhook Info */}
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">Configuração do Webhook</h4>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-muted-foreground">URL:</label>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <code className="neu-well2 flex-1 text-[10px] bg-background px-2 py-1.5 rounded border border-border font-mono truncate">
                    {METAWEBHOOK_URL}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(METAWEBHOOK_URL)}
                    className="px-2 py-1.5 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700 transition-colors"
                  >
                    Copiar
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Token:</label>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <code className="neu-well2 flex-1 text-[10px] bg-background px-2 py-1.5 rounded border border-border font-mono">
                    twms_meta_webhook_2026
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText('twms_meta_webhook_2026')}
                    className="px-2 py-1.5 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700 transition-colors"
                  >
                    Copiar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          {metaSuccess && (
            <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-xs text-green-600 dark:text-green-400">✅ Dispositivo cadastrado!</p>
            </div>
          )}
          {metaError && (
            <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-600 dark:text-red-400">❌ {metaError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleMetaSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Nome *</label>
              <input
                type="text"
                value={metaForm.name}
                onChange={(e) => setMetaForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: WhatsApp Comercial"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Phone Number ID *</label>
                <input
                  type="text"
                  value={metaForm.phoneNumberId}
                  onChange={(e) => setMetaForm(p => ({ ...p, phoneNumberId: e.target.value }))}
                  placeholder="123456789012345"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Business Account ID *</label>
                <input
                  type="text"
                  value={metaForm.businessAccountId}
                  onChange={(e) => setMetaForm(p => ({ ...p, businessAccountId: e.target.value }))}
                  placeholder="987654321098765"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Telefone (opcional)</label>
              <input
                type="text"
                value={metaForm.displayPhone}
                onChange={(e) => setMetaForm(p => ({ ...p, displayPhone: e.target.value }))}
                placeholder="+55 67 99999-9999"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Access Token *</label>
              <textarea
                value={metaForm.accessToken}
                onChange={(e) => setMetaForm(p => ({ ...p, accessToken: e.target.value }))}
                placeholder="Token de acesso do sistema..."
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={metaLoading || !metaForm.name || !metaForm.phoneNumberId || !metaForm.businessAccountId || !metaForm.accessToken}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {metaLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <MetaIcon className="w-4 h-4" />
                )}
                {metaLoading ? 'Salvando...' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ============================================
  // RENDER EVOLUTION
  // ============================================
  return (
    <div className="flex flex-col h-full bg-card neu-flat">
      {/* Header */}
      <div className="neu-flat flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          {evolutionStep !== 'choice' && (
            <button
              onClick={() => {
                setEvolutionStep('choice')
                resetQRState()
                setExistingInstanceName('')
                setExistingInstanceApiKey('')
              }}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
          )}
          <div className="w-9 h-9 rounded-lg bg-green-600 text-white flex items-center justify-center">
            <WhatsAppIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Novo Dispositivo Evolution</h2>
            <p className="text-xs text-muted-foreground">
              {evolutionStep === 'choice' ? 'Selecione o método' :
               evolutionStep === 'new' ? 'Nova instância' : 'Instância existente'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Fechar"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Messages */}
        {evolutionSuccess && (
          <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-xs text-green-600 dark:text-green-400">✅ {evolutionSuccess}</p>
          </div>
        )}
        {evolutionError && (
          <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
            <p className="text-xs text-red-600 dark:text-red-400">❌ {evolutionError}</p>
            <button onClick={() => setEvolutionError(null)} className="text-red-400 hover:text-red-300">
              <FiX className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Choice Step */}
        {evolutionStep === 'choice' && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setEvolutionStep('new')}
              className="neu-card flex flex-col items-center gap-3 p-4 rounded-lg border border-border hover:border-green-500/50 hover:bg-green-500/5 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-green-600 text-white flex items-center justify-center">
                <FiPlus className="w-6 h-6" />
              </div>
              <div className="text-center">
                <p className="font-medium text-sm">Nova Instância</p>
                <p className="text-[10px] text-muted-foreground">Escanear QR Code</p>
              </div>
            </button>

            <button
              onClick={() => setEvolutionStep('existing')}
              className="neu-card flex flex-col items-center gap-3 p-4 rounded-lg border border-border hover:border-blue-500/50 hover:bg-blue-500/5 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                <FiLink className="w-6 h-6" />
              </div>
              <div className="text-center">
                <p className="font-medium text-sm">Inserir Existente</p>
                <p className="text-[10px] text-muted-foreground">Nome + API Key</p>
              </div>
            </button>
          </div>
        )}

        {/* New Instance Step */}
        {evolutionStep === 'new' && (
          <div className="space-y-4">
            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Nome do cliente *</label>
                <input
                  type="text"
                  value={newInstanceName}
                  onChange={(e) => setNewInstanceName(e.target.value)}
                  placeholder="Ex: João Silva"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
                  disabled={isCreatingInstance || !!pendingQRCode}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Telefone (opcional)</label>
                <input
                  type="tel"
                  value={newInstancePhone}
                  onChange={(e) => setNewInstancePhone(e.target.value)}
                  placeholder="5567999999999"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50"
                  disabled={isCreatingInstance || !!pendingQRCode}
                />
              </div>
            </div>

            {/* QR Code Area */}
            <div className="flex items-start gap-4">
              {/* QR Code / Button */}
              <div className="w-32 h-32 flex-shrink-0">
                {!pendingQRCode ? (
                  <button
                    onClick={createEvolutionInstance}
                    disabled={isCreatingInstance || !newInstanceName.trim()}
                    className="w-full h-full flex flex-col items-center justify-center gap-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all disabled:opacity-50"
                  >
                    {isCreatingInstance ? (
                      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiSmartphone className="w-8 h-8" />
                    )}
                    <span className="text-xs font-semibold">{isCreatingInstance ? 'Gerando...' : 'Gerar QR'}</span>
                  </button>
                ) : connectionStatus === 'connected' ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-green-500/10 border-2 border-green-500 rounded-xl">
                    <FiCheck className="w-10 h-10 text-green-500" />
                    <span className="text-xs font-semibold text-green-500">Conectado!</span>
                  </div>
                ) : connectionStatus === 'expired' ? (
                  <button
                    onClick={() => {
                      resetQRState()
                      createEvolutionInstance()
                    }}
                    className="w-full h-full flex flex-col items-center justify-center gap-2 bg-red-500/10 border-2 border-red-500 rounded-xl hover:bg-red-500/20 transition-colors"
                  >
                    <FiX className="w-8 h-8 text-red-500" />
                    <span className="text-xs font-semibold text-red-500">Tentar novamente</span>
                  </button>
                ) : (
                  <div className="w-full h-full bg-white rounded-xl p-2 flex items-center justify-center shadow-lg">
                    <img src={pendingQRCode} alt="QR Code" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>

              {/* Status Info */}
              <div className="flex-1">
                {pendingQRCode && connectionStatus === 'waiting' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                        countdown <= 10 ? 'bg-red-500 text-white' : 'bg-green-500/20 text-green-600'
                      }`}>
                        {countdown}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">Aguardando...</p>
                        <p className="text-xs text-muted-foreground">Escaneie o QR Code</p>
                      </div>
                    </div>
                    {pendingPairingCode && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Código:</span>
                        <span className="neu-well2 font-mono font-bold text-sm bg-secondary/50 px-2 py-1 rounded tracking-wider">
                          {pendingPairingCode}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {connectionStatus === 'connected' && (
                  <div className="flex items-center gap-3">
                    <FiCheck className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-semibold text-sm text-green-500">Conectado!</p>
                      <p className="text-xs text-muted-foreground">Dispositivo pronto</p>
                    </div>
                  </div>
                )}
                {connectionStatus === 'expired' && (
                  <div className="flex items-center gap-3">
                    <FiAlertCircle className="w-6 h-6 text-red-500" />
                    <div>
                      <p className="font-semibold text-sm text-red-500">QR expirado</p>
                      <p className="text-xs text-muted-foreground">Clique para tentar novamente</p>
                    </div>
                  </div>
                )}
                {connectionStatus === 'idle' && !isCreatingInstance && (
                  <div className="text-xs text-muted-foreground">
                    <p className="mb-2">Preencha o nome e clique em "Gerar QR" para conectar um novo dispositivo WhatsApp.</p>
                    <p>Você terá 60 segundos para escanear o código com seu celular.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Existing Instance Step */}
        {evolutionStep === 'existing' && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Nome da instância *</label>
              <input
                type="text"
                value={existingInstanceName}
                onChange={(e) => setExistingInstanceName(e.target.value)}
                placeholder="Nome exato da instância na Evolution"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                disabled={isSavingExisting}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">API Key *</label>
              <input
                type="password"
                value={existingInstanceApiKey}
                onChange={(e) => setExistingInstanceApiKey(e.target.value)}
                placeholder="API Key da instância"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                disabled={isSavingExisting}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveExistingInstance}
                disabled={isSavingExisting || !existingInstanceName.trim() || !existingInstanceApiKey.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingExisting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FiCheck className="w-4 h-4" />
                )}
                {isSavingExisting ? 'Salvando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
